<i18n>
{
	"en": {
		"title": "Select stretch",
		"desc": "Which stretch of the order ({orderNames}) should be assigned to trip template {tripName}?",
		"fromTripStart": "From Trip Start",
		"fromPickupLocation": "From Pickup Location",
		"toTripEnd": "To Trip End",
		"toDeliveryLocation": "To Delivery Location",
		"addToTemplate": "Add to Trip",
		"multipleLocations": "Multiple Locations",
		"customFromLocation": "Custom Pickup Location",
		"customToLocation": "Custom Unloading Location",
		"useCustomFromLocation": "Use custom pickup location",
		"useCustomToLocation": "Use custom unloading location"
	},
	"no": {
		"title": "Velg strekninger",
		"desc": "Hvilke deler av oppdraget ({orderNames}) skal legges til på turmalen {tripName}?",
		"fromTripStart": "Fra turstart",
		"fromPickupLocation": "Fra hentested",
		"toTripEnd": "Til turslutt",
		"toDeliveryLocation": "Til leveringssted",
		"addToTemplate": "Legg til på tur",
		"multipleLocations": "Flere steder",
		"customFromLocation": "Egendefinert hentested",
		"customToLocation": "Egendefinert leveringssted",
		"useCustomFromLocation": "Bruk egendefinert hentested",
		"useCustomToLocation": "Bruk egendefinert leveringssted"
	}
}
</i18n>
<template>
	<div>
		<div class="dialog-top">
			{{ $t('desc', { orderNames, tripName }) }}
		</div>
		<div class="dialog-mid">
			<div class="dialog-left">
				<t-stretch-select
					v-if="fromLocations && toLocations"
					:fromLocations="fromLocations"
					:fromIndex="from"
					:toLocations="toLocations"
					:toIndex="to"
					:allowCustomFrom="allowCustomFrom"
					:allowCustomTo="allowCustomTo"
					@changeFromLocation="changeFromLocation"
					@changeToLocation="changeToLocation"
				>
					<template #customFrom>
						<GlobalAddressInput
							:title="$t('customFromLocation')"
							:value="customFromLocation"
							@change="onCustomFromSelected"
						/>
					</template>
					<template #customTo>
						<GlobalAddressInput
							:title="$t('customToLocation')"
							:value="customToLocation"
							@change="onCustomToSelected"
						/>
					</template>
				</t-stretch-select>
			</div>
			<div class="dialog-right">
				<t-switch
					v-model="allowCustomFrom"
					:label="$t('useCustomFromLocation')"
					class="custom-from-switch"
					@change="customFromToggle"
				/>
				<t-switch
					v-model="allowCustomTo"
					:label="$t('useCustomToLocation')"
					class="custom-to-switch"
					@change="customToToggle"
				/>
			</div>
		</div>
	</div>
</template>
<script>
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';
import TStretchSelect from '@/components/evergreen/timpex/inputs/TStretchSelect';

export default {
	name: 'SelectStretches',
	components: { TStretchSelect, GlobalAddressInput },
	props: {
		tripTemplate: Object,
		orderTemplates: Array,
	},
	data() {
		return {
			fromLocations: [],
			toLocations: [],
			customFromLocation: {},
			customToLocation: {},
			from: 0,
			to: 0,
			allowCustomFrom: false,
			allowCustomTo: false,
			defaultStartIndex: 0,
			defaultEndIndex: 0,
		};
	},
	computed: {
		tripName() {
			return this.tripTemplate && 'name' in this.tripTemplate ? this.tripTemplate.name : '';
		},
		orderNames() {
			return this.orderTemplates ? this.orderTemplates.map(x => x.name).join(', ') : '';
		}
	},
	mounted() {
		this.processLocations();
	},
	methods: {
		processLocations() {
			this.fromLocations = [];
			this.toLocations = [];
			if (this.tripTemplate.fromAddress) {
				this.fromLocations.push({
					label: this.$t('fromTripStart'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.tripTemplate.fromAddress.name,
					option: 'trip'
				});
				this.defaultStartIndex = 0;
			}
			if (this.tripTemplate.toAddress) {
				this.toLocations.push({
					label: this.$t('toTripEnd'),
					labelClass: 'stretch-details-label',
					primaryClass: 'stretch-details-primary',
					name: this.tripTemplate.toAddress.name,
					option: 'trip'
				});
				this.defaultEndIndex = 0;
			}

			if (this.orderTemplates) {
				let despatchParty = this.orderTemplates[0].despatchParty || this.orderTemplates[0].consignor;
				if (despatchParty) {
					let fromName = this.orderTemplates.length > 1 ? this.$t('multipleLocations') : despatchParty.name;
					this.fromLocations.push({
						label: this.$t('fromPickupLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: fromName,
						option: 'despatchParty'
					});
					if (this.tripTemplate.type && this.tripTemplate.type.internalCode && this.tripTemplate.type.internalCode.toUpperCase() === 'L')
						this.defaultStartIndex = this.fromLocations.length - 1;
				}
				let deliveryParty = this.orderTemplates[0].deliveryParty || this.orderTemplates[0].consignee;
				if (deliveryParty) {
					let toName = this.orderTemplates.length > 1 ? this.$t('multipleLocations') : deliveryParty.name;
					this.toLocations.push({
						label: this.$t('toDeliveryLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: toName,
						option: 'deliveryParty'
					});
				}
			}
			this.changeFromLocation(this.defaultStartIndex);
			this.changeToLocation(this.defaultEndIndex);
		},
		addToTemplate() {
			let despatchOption = this.from >= this.fromLocations.length ? 'custom' : this.fromLocations[this.from].option;
			let deliveryOption = this.to >= this.toLocations.length ? 'custom' : this.toLocations[this.to].option;
			this.$emit('addToTemplate',{
				despatchOption: despatchOption,
				deliveryOption: deliveryOption,
				customDespatchParty: this.customFromLocation,
				customDeliveryParty: this.customToLocation
			});
		},
		changeFromLocation(index) {
			this.from = index;
		},
		changeToLocation(index) {
			this.to = index;
		},
		onCustomFromSelected(value) {
			this.customFromLocation = value;
		},
		onCustomToSelected(value) {
			this.customToLocation = value;
		},
		customFromToggle(value) {
			if (value) {
				this.changeFromLocation(this.fromLocations.length);
			} else {
				this.changeFromLocation(this.defaultStartIndex);
			}
		},
		customToToggle(value) {
			if (value) {
				this.changeToLocation(this.toLocations.length);
			} else {
				this.changeToLocation(this.defaultEndIndex);
			}
		}
	},
};
</script>

<style >
.stretch-details-label {
	font-size: 11px;
	font-weight: 500;
}
.stretch-details-primary {
	font-size: 12px;
}
</style>

<style lang="scss" scoped>
.dialog-mid {
	display: grid;
	column-gap: 20px;
	grid-template-columns: 300px 240px;
	margin-top:15px;
	padding-bottom: 20px;
	.custom-switches {
		display: grid;
		grid-row-gap: 10px;
		grid-template-rows: repeat(2, min-content);
		padding-top:20px;
	}
}

.dialog-right {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
}

.custom-from-switch {
	margin-top: 40px;
}

.custom-to-switch {
	margin-bottom: 30px;
}
</style>
